import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  PageWrapper,
  Title,
  Note,
  Table,
  Row,
  HeaderRow,
  Cell,
  StatusPaid,
  StatusUnpaid,
  PayButton,
  DownloadButton,
  PaginationWrapper,
  NavButton,
  PageButton,
  SearchInput,
  FilterBar,
  Select,
  TableWrapper,
  HeaderRowAdmin,
  RowAdmin,
} from "../../../style/TransactionHistory_Style";
import PaymentService from "../../../services/api/payment.service";
import {
  EQrcodeRequest,
  IGetPageHistoryTransactionByUser,
} from "../../../models/payment";
import { toast } from "../../../utils/notify";
import { useTranslation } from "react-i18next";
import {
  ButtonPaymentModal,
  CloseButton,
  PreviewContent,
  PreviewHeader,
  PreviewModal,
  PreviewOverlay,
  PreviewTitle,
} from "../../../style/AIToolsPage_Style";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { envConfig } from "../../../config/env.config";
import { EPaymentStatus, HistoryTranslate } from "../../main/AiToolsPage";
import UserService from "../../../services/api/user.service";

export interface TransactionItem {
  id: string;
  fileName: string;
  fileSize: number; // bytes
  fromLang: string;
  toLang: string;
  isPaid: boolean;
}

const formatFileSize = (bytes: number) => {
  return bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const AdminTransactionHistory: React.FC = () => {
  const PAGE_SIZE = 10;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<EPaymentStatus | "all">("all");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [totalPages, setTotalPages] = useState(0);
  const { t } = useTranslation();
  const [historyTranslate, setHistoryTranslate] = useState<HistoryTranslate>();
  const [historyTranslates, setHistoryTranslates] =
    useState<HistoryTranslate[]>();

  useEffect(() => {
    GetPageHistoryTransactionByUser();
  }, [page, search, status]);

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const GetPageHistoryTransactionByUser = async () => {
    const params: IGetPageHistoryTransactionByUser = {
      CurrentPage: page,
      PageSize: PAGE_SIZE,
      KeySearch: search,
      Status: status === "all" ? undefined : status,
    };

    const res = await UserService.getPageFile(params);

    if (res.success) {
      setHistoryTranslates(res.items);
      setTotalCount(res.totalCount);
      setTotalPages(res.totalPages); // 👈 backend trả về
    } else {
      toast(res.message, "error");
    }
  };

  const downloadBase64File = (base64: string, fileName: string) => {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "application/octet-stream";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    const blob = new Blob([u8arr], { type: mime });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName; // 👈 tên file tải về
    document.body.appendChild(a);
    a.click(); // 👈 trigger download
    document.body.removeChild(a);

    URL.revokeObjectURL(url); // cleanup
  };

  const handleGetQrcode = async (orderId: string, price: number) => {
    const data: EQrcodeRequest = {
      orderId: orderId,
      price: price, //previewFile.price ?? 0,
    };

    const qrcode = await PaymentService.GetQrCode(data);

    if (qrcode.success) {
      window.open(qrcode.data, "_blank");
    } else {
      toast(t("transaction.getQrFail"), "error");
    }
  };

  const formatVND = (value: number): string => {
    return new Intl.NumberFormat("vi-VN").format(value);
  };

  const formatDate = (date?: string | Date): string => {
    if (!date) return "";

    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const createOrder = async (
    customOrderId: string | undefined,
    price?: number,
  ) => {
    const res = await fetch(
      envConfig.API_ENDPOINT +
        `/paypal/create-order?customOrderId=${customOrderId}&price=${price}`,
      {
        method: "POST",
      },
    );

    if (!res.ok) {
      toast("Tạo thanh toán thất bại", "error");
      return;
    }

    const data = await res.json();

    return data.id;
  };

  const onApprove = async (data: any, customOrderId?: string) => {
    await fetch(
      envConfig.API_ENDPOINT +
        `/paypal/capture-order?orderId=${data.orderID}&customOrderId=${customOrderId}`,
      { method: "POST" },
    );

    toast("Thanh toán PayPal thành công", "success");

    setShowPaymentModal(false);

    window.open(
      "https://docme.vn/transaction?orderId=" + customOrderId,
      "_blank",
    );
  };

  const getPagination = (current: number, total: number) => {
    const delta = 2; // số page xung quanh current
    const range: (number | string)[] = [];

    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    // luôn có trang 1
    range.push(1);

    if (left > 2) {
      range.push("...");
    }

    for (let i = left; i <= right; i++) {
      range.push(i);
    }

    if (right < total - 1) {
      range.push("...");
    }

    // luôn có trang cuối
    if (total > 1) {
      range.push(total);
    }

    return range;
  };

  return (
    <PageWrapper>
      <Title>
        {t("transaction.title")} - tổng {totalCount}
      </Title>

      <FilterBar>
        <SearchInput
          placeholder={t("transaction.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as EPaymentStatus | "all")}>
          <option value="all">{t("transaction.status.all")}</option>
          <option value={EPaymentStatus.success}>
            {t("transaction.status.paid")}
          </option>
          <option value={EPaymentStatus.pending}>
            {t("transaction.status.unpaid")}
          </option>
        </Select>
      </FilterBar>
      <TableWrapper>
        <Table>
          <HeaderRowAdmin>
            <Cell>{t("transaction.table.fileName")}</Cell>
            <Cell>{t("transaction.table.fileSize")}</Cell>
            <Cell>{t("transaction.table.language")}</Cell>
            <Cell>{t("transaction.table.status")}</Cell>
            <Cell>{t("transaction.table.price")}</Cell>
            <Cell>{t("transaction.table.expirationDate")}</Cell>
            <Cell>Ngày tạo</Cell>
            {/* <Cell>{t("transaction.table.action")}</Cell> */}
          </HeaderRowAdmin>

          {historyTranslates &&
            historyTranslates.map((item) => (
              <RowAdmin key={item.id}>
                <Cell>{item.fileName}</Cell>
                <Cell>{formatFileSize(item.fileSize)}</Cell>
                <Cell>
                  {item.from} → {item.to}
                </Cell>
                <Cell>
                  {item.status === EPaymentStatus.success ||
                  item.status === EPaymentStatus.translate_Completed ||
                  item.status === EPaymentStatus.translate_Processing ? (
                    <StatusPaid>{t("transaction.status.paid")}</StatusPaid>
                  ) : (
                    <StatusUnpaid>
                      {t("transaction.status.unpaid")}
                    </StatusUnpaid>
                  )}
                </Cell>
                <Cell>{item?.price && formatVND(item?.price)} VNĐ</Cell>
                <Cell>
                  {item?.expirationDate && formatDate(item?.expirationDate)}
                </Cell>
                <Cell>
                  {item?.createdDate && formatDate(item?.createdDate)}
                </Cell>
                {/* <Cell>
                  {item.status === EPaymentStatus.success ? (
                    <DownloadButton
                      onClick={() =>
                        item?.status === EPaymentStatus.success
                          ? item?.fileDownloadBase64 &&
                            downloadBase64File(
                              item.fileDownloadBase64,
                              item.fileName,
                            )
                          : toast(
                              <>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  fill="currentColor"
                                  viewBox="0 0 16 16">
                                  <path d="M7.005 3.1a1 1 0 1 1 1.99 0l-.388 6.35a.61.61 0 0 1-1.214 0z" />
                                  <path d="M7 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0" />
                                </svg>
                                <span>{t("transaction.mustPayFirst")}</span>
                              </>,
                              "info",
                            )
                      }>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        className="bi bi-download">
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                      </svg>
                      {t("transaction.download")}
                    </DownloadButton>
                  ) : (
                    <PayButton
                      onClick={() => {
                        setHistoryTranslate(item);
                        setShowPaymentModal(true);
                      }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        strokeWidth="0.8"
                        className="bi bi-credit-card"
                        viewBox="0 0 16 16">
                        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1z" />
                        <path d="M2 10a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
                      </svg>
                      {t("transaction.pay")}

                    </PayButton>
                  )}
                </Cell> */}
              </RowAdmin>
            ))}
        </Table>
      </TableWrapper>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationWrapper>
          <NavButton disabled={page === 1} onClick={() => setPage(page - 1)}>
            {t("pagination.prev")}
          </NavButton>

          {getPagination(page, totalPages).map((item, i) =>
            item === "..." ? (
              <span key={i} style={{ padding: "0 8px" }}>
                ...
              </span>
            ) : (
              <PageButton
                key={i}
                active={page === item}
                onClick={() => setPage(item as number)}>
                {item}
              </PageButton>
            ),
          )}

          <NavButton
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}>
            {t("pagination.next")}
          </NavButton>
        </PaginationWrapper>
      )}

      {showPaymentModal && (
        <PreviewOverlay>
          <PreviewModal style={{ maxWidth: 420 }}>
            <PreviewHeader>
              <PreviewTitle>{t("preview.choose_payment_method")}</PreviewTitle>
              <CloseButton onClick={() => setShowPaymentModal(false)}>
                ✕
              </CloseButton>
            </PreviewHeader>

            <PreviewContent
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                padding: 20,
              }}>
              {/* VietQR */}
              <ButtonPaymentModal
                sx={{ justifyContent: "center" }}
                onClick={() => {
                  historyTranslate &&
                    handleGetQrcode(
                      historyTranslate.orderId,
                      historyTranslate.price,
                    );
                }}>
                <img
                  style={{
                    height: "20px",
                    filter: "drop-shadow(0px 1px 1px white)",
                  }}
                  src="/icon/VietQR.46a78cbb.png"
                />{" "}
                🇻🇳 Thanh toán VietQR
              </ButtonPaymentModal>

              {/* PayPal */}
              <div style={{ marginTop: 10 }}>
                <PayPalButtons
                  style={{ layout: "vertical" }}
                  createOrder={() =>
                    createOrder(
                      historyTranslate?.orderId,
                      historyTranslate?.price,
                    )
                  }
                  onApprove={(data) =>
                    onApprove(data, historyTranslate?.orderId)
                  }
                />
              </div>
            </PreviewContent>
          </PreviewModal>
        </PreviewOverlay>
      )}
    </PageWrapper>
  );
};
